// ExcersiceService.js — migrated to the OpenAPI-generated client.
//
// Exercises are served by TWO controllers sharing one base (BaseExerciseController):
//   - AdminExerciseController  (/admin/exercise)  — a company's own exercises (ADMIN/TRAINER).
//   - SystemExerciseController (/system/exercise) — the global/system exercises (SYSTEM_ADMIN).
// A super-admin has no company, so it must talk to /system/exercise (a company endpoint would 403).
// The client only picks the route (server scopes by principal). Role comes from the DB-derived
// authorities (reliable for legacy tokens too), not the JWT claim — see isSuperAdminAuthority.
//
// AUTHORITY SWITCH PRESERVED: the SDK exposes a separate function per path
// (adminExerciseController* vs systemExerciseController*). Each migrated function dispatches to
// the system fn when isSuperAdminAuthority() and the admin fn otherwise — the exact same branch
// the legacy exerciseBase() encoded.
//
// Adapter-first: every function keeps its pre-migration signature + return shape. Callers read
// the payload off `res.data` (the exercise object, the Spring Page, or the flat check-name
// result — no {..,body} envelope on these endpoints), so we return `{data}`. Auth + X-Timezone
// are injected by configureApi's interceptor, so the per-call token reads are gone.
// GROUP-ADMIN LIBRARY: a COMPANY_GROUP_ADMIN owns a private library served by a THIRD controller,
// GroupAdminExerciseController (/groupAdmin/exercise) — owner-scoped, assigning only to the group
// admin's authorized companies. Those endpoints are not in the OpenAPI snapshot yet, so (mirroring the
// resetToOriginal super-admin branch below) the group-admin branch talks to httpService directly.
// TODO(openapi-migration): regenerate the SDK (npm run gen:api:live) once /groupAdmin/* ships to stage,
// then swap these httpService calls for the generated groupAdminExerciseController* functions.
import httpService from "./HttpService";
import {isSuperAdminAuthority, isCompanyGroupAdminAuthority} from "../contexts/AuthContext";
import {
    adminExerciseControllerGetByTypes,
    systemExerciseControllerGetByTypes,
    adminExerciseControllerSaveExerciseV2,
    systemExerciseControllerSaveExerciseV2,
    adminExerciseControllerGetExercise,
    systemExerciseControllerGetExercise,
    adminExerciseControllerGet,
    systemExerciseControllerGet,
    adminExerciseControllerResetToOriginal,
    adminExerciseControllerCheckExerciseName,
    systemExerciseControllerCheckExerciseName,
    adminExerciseControllerDeleteExercise,
    systemExerciseControllerDeleteExercise,
} from '../api/generated';

const GROUP_ADMIN_EXERCISE_BASE = "groupAdmin/exercise";
// A COMPANY_GROUP_ADMIN MANAGES a private library (owner-scoped, GROUP_ADMIN_EXERCISE_BASE) but must be
// able to BUILD workouts with the System library too. Builder call sites pass fromLibrary=true to browse
// system + own exercises via these read-only endpoints; the management pages leave it false.
const GROUP_ADMIN_EXERCISE_LIBRARY_BASE = "groupAdmin/exercise/library";

const authToken = () => localStorage.getItem("auth");

// Build a `?a=1&b=2` query string, skipping null/undefined values (GET bodies are ignored by the server).
const toQuery = (params) => {
    const search = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
        if (value !== null && value !== undefined) search.append(key, value);
    });
    const encoded = search.toString();
    return encoded ? `?${encoded}` : "";
};

// Re-throw an SDK error as an axios-shaped error so existing catch handlers that read
// `error.response?.data?.errorCode` / `.message` keep working exactly as they did on axios.
const asAxiosError = (error, response) => {
    /** @type {any} */
    const err = new Error(error?.errorMessage || error?.message || 'Request failed');
    err.response = {data: error, status: response?.status};
    return err;
};

let exerciseByTypeCache = {};

let exerciseByIdCache = {};

export const getByTypes = async (targetMuscle) => {
    if (!exerciseByTypeCache[targetMuscle]) {
        if (isCompanyGroupAdminAuthority()) {
            const res = await httpService.get(
                `${GROUP_ADMIN_EXERCISE_BASE}/type${toQuery({targetMuscle})}`, null, authToken());
            exerciseByTypeCache[targetMuscle] = {data: res.data};
            return exerciseByTypeCache[targetMuscle];
        }
        const sdkFn = isSuperAdminAuthority()
            ? systemExerciseControllerGetByTypes
            : adminExerciseControllerGetByTypes;
        const {data, error, response} = await sdkFn({query: {targetMuscle}});
        if (error) throw asAxiosError(error, response);
        exerciseByTypeCache[targetMuscle] = {data};
    }
    return exerciseByTypeCache[targetMuscle];
};

/**
 * Save exercise using JSON with CDN URLs (new approach)
 * Files are uploaded separately via MediaUploadService, and CDN URLs are passed in exerciseData
 *
 * @param {Object} exerciseData - Exercise data including CDN URLs for images/video
 */
export const saveExercise = async (exerciseData) => {
    exerciseByTypeCache = {};
    exerciseByIdCache = {};

    if (isCompanyGroupAdminAuthority()) {
        const res = await httpService.post(`${GROUP_ADMIN_EXERCISE_BASE}/v2`, exerciseData, authToken());
        return {data: res.data};
    }

    const sdkFn = isSuperAdminAuthority()
        ? systemExerciseControllerSaveExerciseV2
        : adminExerciseControllerSaveExerciseV2;
    const {data, error, response} = await sdkFn({body: exerciseData});
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const getExercise = async (id, fromLibrary = false) => {
    // Builder contexts pass fromLibrary=true so a group admin can resolve SYSTEM exercises (not just their
    // own) when enriching a program. Cache separately per scope so the two views never cross-contaminate.
    const cacheKey = fromLibrary ? `lib:${id}` : `${id}`;
    if (!exerciseByIdCache[cacheKey]) {
        if (isCompanyGroupAdminAuthority()) {
            const base = fromLibrary ? GROUP_ADMIN_EXERCISE_LIBRARY_BASE : GROUP_ADMIN_EXERCISE_BASE;
            const res = await httpService.get(`${base}/${id}`, null, authToken());
            exerciseByIdCache[cacheKey] = {data: res.data};
            return exerciseByIdCache[cacheKey];
        }
        const sdkFn = isSuperAdminAuthority()
            ? systemExerciseControllerGetExercise
            : adminExerciseControllerGetExercise;
        const {data, error, response} = await sdkFn({path: {id}});
        if (error) throw asAxiosError(error, response);
        exerciseByIdCache[cacheKey] = {data};
    }

    return exerciseByIdCache[cacheKey];
};

export const getExercises = async (pageId = 0, pageSize = 20,
    keyword,
    resistanceType,
    targetMuscle,
    companyId,
    catalyst,
    fromLibrary = false,
    scope) => {
    // NOTE: companyId was dropped from the server param set in the company-scoping sweep
    // (scoping is principal-derived now), so it is no longer sent — matches the current spec.
    const query = {pageId, pageSize};

    if (keyword) {
        query.keyword = keyword;
    }

    if (resistanceType?.value !== null && resistanceType?.value !== undefined) {
        query.resistanceType = resistanceType.value;
    }

    if (targetMuscle?.value !== null && targetMuscle?.value !== undefined) {
        query.targetMuscle = targetMuscle.value;
    }

    if (catalyst != null) {
        query.catalyst = catalyst;
    }

    if (isCompanyGroupAdminAuthority()) {
        // Builder picker (fromLibrary) browses System + own exercises. The management page (fromLibrary
        // false) is the group admin's library — System exercises + their own — filterable via `scope`
        // (ALL | SYSTEM | OWN); the builder endpoint ignores scope.
        const base = fromLibrary ? GROUP_ADMIN_EXERCISE_LIBRARY_BASE : GROUP_ADMIN_EXERCISE_BASE;
        if (!fromLibrary && scope) {
            query.scope = scope;
        }
        const res = await httpService.get(`${base}${toQuery(query)}`, null, authToken());
        return {data: res.data};
    }

    const sdkFn = isSuperAdminAuthority()
        ? systemExerciseControllerGet
        : adminExerciseControllerGet;
    const {data, error, response} = await sdkFn({query});
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const resetToOriginal = async (exerciseId) => {
    exerciseByTypeCache = {};
    exerciseByIdCache = {};

    if (isSuperAdminAuthority()) {
        // TODO(openapi-migration): no /system/exercise/{id}/reset in the OpenAPI spec — only
        // the admin route exists. Keep the super-admin branch on httpService until the server
        // exposes a system reset endpoint. (The admin branch below is fully on the SDK.)
        const token = localStorage.getItem("auth");
        return await httpService.post(`system/exercise/${exerciseId}/reset`, null, token);
    }

    const {data, error, response} = await adminExerciseControllerResetToOriginal({path: {id: exerciseId}});
    if (error) throw asAxiosError(error, response);
    return {data};
};

export const checkExerciseNameExists = async (name, nameHeb, excludeId = null) => {
    const query = {};
    if (name) query.name = name;
    if (nameHeb) query.nameHeb = nameHeb;
    if (excludeId) query.excludeId = excludeId;

    if (isCompanyGroupAdminAuthority()) {
        const res = await httpService.get(
            `${GROUP_ADMIN_EXERCISE_BASE}/check-name${toQuery(query)}`, null, authToken());
        return {data: res.data};
    }

    const sdkFn = isSuperAdminAuthority()
        ? systemExerciseControllerCheckExerciseName
        : adminExerciseControllerCheckExerciseName;
    const {data, error, response} = await sdkFn({query});
    if (error) throw asAxiosError(error, response);
    return {data};
};

/**
 * Delete an exercise by ID
 * Super Admin can delete any exercise
 * Company Admin can only delete exercises they created
 *
 * @param {number} exerciseId - The ID of the exercise to delete
 */
export const deleteExercise = async (exerciseId) => {
    // Clear caches since exercise data is changing
    exerciseByTypeCache = {};
    exerciseByIdCache = {};

    if (isCompanyGroupAdminAuthority()) {
        const res = await httpService.delete(`${GROUP_ADMIN_EXERCISE_BASE}/${exerciseId}`, null, authToken());
        return {data: res.data};
    }

    const sdkFn = isSuperAdminAuthority()
        ? systemExerciseControllerDeleteExercise
        : adminExerciseControllerDeleteExercise;
    const {data, error, response} = await sdkFn({path: {id: exerciseId}});
    if (error) throw asAxiosError(error, response);
    return {data};
};
